<template>
  <main v-if="data" class="container">
    <div class="row">
      <div class="col-12 col-xs-10 col-md-12 col-lg-10 col-xl-8 col-xxl-7 mx-auto mt-4 mt-md-5">
        <div class="d-flex align-items-center mb-3">
          <h1 class="h2">
            Flight {{ selectedFlight.flight_number }} from {{ selectedFlight.origin.city.name }} to {{ selectedFlight.destination.city.name }}
          </h1>
        </div>
        <div class="mb-2 d-flex">
          <div class="route me-4">
            <div class="step">
              <NuxtImg
                  alt="Origin icon"
                  width="11"
                  height="11"
                  src="img/arrow.up.forward.svg"
              />
            </div>
            <div class="step">
              <NuxtImg
                  alt="Destination icon"
                  width="11"
                  height="11"
                  src="img/arrow.down.forward.svg"
              />
            </div>
          </div>
          <div class="w-100">
            <div class="mb-3 d-flex align-items-center">
              <div>
                <h2 class="mb-1 h5 small text-uppercase opacity-75">
                  <NuxtLink
                      class="inherit"
                      :to="{ name: 'vluchten-vanaf', params: { name: selectedFlight.origin.city.slug_nl, iata: selectedFlight.origin.iata.toLowerCase() } }"
                      :title="'Vluchten vanaf ' + selectedFlight.origin.city.name">
                    {{ selectedFlight.origin.city.name }}
                  </NuxtLink>
                </h2>
                <p class="h3 mb-1">{{ selectedFlight.origin.iata }}</p>
                <p class="mb-0 small opacity-75">{{ selectedFlight.origin.name_en }}</p>
              </div>
              <div class="ms-auto text-end">
                <p class="mb-1 h5 small text-uppercase opacity-75">
                  {{ formatDate(selectedFlight.scheduled_departure) }}
                </p>
                <p v-if="(selectedFlight.estimated_departure || selectedFlight.real_departure) && (selectedFlight.estimated_departure || selectedFlight.real_departure !== selectedFlight.scheduled_departure)" class="mb-0">
                  <span class="h4 me-1 text-decoration-line-through opacity-75">{{ formatTime(selectedFlight.scheduled_departure) }}</span>
                  <span class="h3">{{ formatTime(selectedFlight.estimated_departure ?? selectedFlight.real_departure) }}</span>
                  <span v-if="selectedFlight.departure_delay && selectedFlight.departure_delay > 0" class="h6 text-danger d-block d-md-inline pt-1 pt-md-n1 ms-1">+{{ selectedFlight.departure_delay }} min.</span>
                  <span v-else-if="selectedFlight.departure_delay && selectedFlight.departure_delay < 0" class="h6 text-success d-block d-md-inline pt-1 pt-md-n1 ms-1">{{ selectedFlight.departure_delay }} min.</span>
                </p>
                <p v-else class="mb-0 h3">
                  {{ formatTime(selectedFlight.scheduled_departure) }}
                </p>
                <p class="small mt-1 mb-0 opacity-75">
                  UTC{{ getTimezone(selectedFlight.scheduled_departure) }}
                </p>
              </div>
            </div>
            <div class="divider">
              <span>{{ createFormattedDistance(selectedFlight?.flight_distance) }} · {{ createFormattedTime(selectedFlight.flight_duration) }}</span>
            </div>
            <div class="mt-3 d-flex align-items-center">
              <div>
                <h2 class="mb-1 h5 small text-uppercase opacity-75">
                  <NuxtLink
                      class="inherit"
                      :to="{ name: 'vluchten-naar', params: { name: selectedFlight.destination.city.slug_nl, iata: selectedFlight.destination.iata.toLowerCase() } }"
                      :title="'Vluchten naar ' + selectedFlight.destination.city.name">
                    {{ selectedFlight.destination.city.name }}
                  </NuxtLink>
                </h2>
                <p class="h3 mb-1">{{ selectedFlight.destination.iata }}</p>
                <p class="mb-0 small opacity-75">{{ selectedFlight.destination.name_en }}</p>
              </div>
              <div class="ms-auto text-end flex-shrink-0">
                <p class="mb-1 h5 small text-uppercase opacity-75">
                  {{ formatDate(selectedFlight.scheduled_arrival) }}
                </p>
                <p v-if="(selectedFlight.estimated_arrival || selectedFlight.real_arrival) && (selectedFlight.estimated_arrival || selectedFlight.real_arrival !== selectedFlight.scheduled_arrival)" class="mb-0">
                  <span class="h4 me-1 text-decoration-line-through opacity-75">{{ formatTime(selectedFlight.scheduled_arrival) }}</span>
                  <span class="h3">{{ formatTime(selectedFlight.estimated_arrival ?? selectedFlight.real_arrival) }}</span>
                  <span v-if="selectedFlight.arrival_delay && selectedFlight.arrival_delay > 0" class="h6 text-danger d-block d-md-inline pt-1 pt-md-n1 ms-1">+{{ selectedFlight.arrival_delay }} min.</span>
                  <span v-else-if="selectedFlight.arrival_delay && selectedFlight.arrival_delay < 0" class="h6 text-success d-block d-md-inline pt-1 pt-md-n1 ms-1">{{ selectedFlight.arrival_delay }} min.</span>
                </p>
                <p v-else class="mb-0 h3">
                  {{ formatTime(selectedFlight.scheduled_arrival) }}
                </p>
                <p class="small mt-1 mb-0 opacity-75">
                  UTC{{ getTimezone(selectedFlight.scheduled_arrival) }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="row mb-2">
          <div class="col-12 mt-4">
            <div class="row g-2 mb-4">
              <div class="col-12">
                <h2 class="h6 small fw-bold text-uppercase text-primary mb-1">Flight information</h2>
              </div>
              <div class="col-12">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Flight Status</h3>
                  <p v-if="selectedFlight.diverted_to" class="mb-0 fw-bold mb-n1 text-danger">
                    Diverted to {{ selectedFlight.diverted_to.city.name_nl }} ({{ selectedFlight.diverted_to.iata }})
                  </p>
                  <p v-else class="mb-0 fw-bold mb-n1" :class="generateStatusClass(selectedFlight)">
                    <span class="live-animation position-relative" style="top: -2px" v-if="selectedFlight.status === 'Live'">●</span>
                    {{ generateStatus(selectedFlight) }}
                  </p>
                </div>
              </div>
              <div class="col-12" v-if="selectedFlight.status === 'Landed' && selectedFlight.arrival_delay && selectedFlight.arrival_delay > 120">
                <div class="alert alert-danger mb-0" role="alert">
                  <p class="mb-1">Flight {{ selectedFlight.flight_number }} on {{ moment(selectedFlight.scheduled_departure).format('D MMM YYYY') }} had a delay of more than <u>{{ selectedFlight.arrival_delay }} minutes</u>. Were you on this flight?</p>
                  <p class="mb-0">You could get compensation. <a :title="selectedFlight.airline.short_name + ' compensation calculation'" :href="'https://www.airhelp.com/nl/?utm_medium=affiliate&utm_source=pap&utm_campaign=aff-646c6e21f27d6&utm_content=&a_aid=646c6e21f27d6&a_bid=5c94479f&data1=' + selectedFlight.flight_number + '&chan=rewardsflights'" target="_blank" rel="nofollow noopener" class="fw-bold text-primary text-white opacity-80">Click here to calculate your compensation</a>.</p>
                </div>
              </div>
              <div class="col-12" v-else-if="selectedFlight.status === 'Canceled' ">
                <div class="alert alert-danger mb-0" role="alert">
                  <p class="mb-1">Flight {{ selectedFlight.flight_number }} on {{ moment(selectedFlight.scheduled_departure).format('D MMM YYYY') }} has been canceled. Were you on this flight?</p>
                  <p class="mb-0">You could get compensation. <a :title="selectedFlight.airline.short_name + ' compensation calculation'" :href="'https://www.airhelp.com/nl/?utm_medium=affiliate&utm_source=pap&utm_campaign=aff-646c6e21f27d6&utm_content=&a_aid=646c6e21f27d6&a_bid=5c94479f&data1=' + selectedFlight.flight_number + '&chan=rewardsflights'" target="_blank" rel="nofollow noopener" class="fw-bold text-primary text-white opacity-80">Click here to calculate your compensation</a>.</p>
                </div>
              </div>
              <div class="col-12" v-else-if="selectedFlight.diverted_to">
                <div class="alert alert-danger mb-0" role="alert">
                  <p class="mb-1">Flight {{ selectedFlight.flight_number }} on {{ moment(selectedFlight.scheduled_departure).format('D MMM YYYY') }} has been diverted. Were you on this flight?</p>
                  <p class="mb-0">You could get compensation. <a :title="selectedFlight.airline.short_name + ' compensation calculation'" :href="'https://www.airhelp.com/nl/?utm_medium=affiliate&utm_source=pap&utm_campaign=aff-646c6e21f27d6&utm_content=&a_aid=646c6e21f27d6&a_bid=5c94479f&data1=' + selectedFlight.flight_number + '&chan=rewardsflights'" target="_blank" rel="nofollow noopener" class="fw-bold text-primary text-white opacity-80">Click here to calculate your compensation</a>.</p>
                </div>
              </div>
              <div class="col-6">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Airline</h3>
                  <p class="mb-0 fw-bold mb-n1">
                    <NuxtImg
                        height="18"
                        v-if="selectedFlight.airline.logo_small_url"
                        class="me-1 mt-n1"
                        loading="lazy"
                        :src="selectedFlight.airline.logo_small_url"
                        :alt="selectedFlight.airline.name + ' icon'"
                    />
                    <NuxtLink
                        :to="{ name: 'airlines-slug', params: { slug: selectedFlight.airline.slug }}"
                        class="inherit fw-bold ms-1"
                        :title="selectedFlight.airline.name">
                      {{ selectedFlight.airline.name }}
                    </NuxtLink>
                  </p>
                </div>
              </div>
              <div class="col-6">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Operator</h3>
                  <p class="mb-0 fw-bold mb-n1"><NuxtImg
                      height="18"
                      v-if="selectedFlight.operator.logo_small_url"
                      class="me-2 mt-n1"
                      loading="lazy"
                      :src="selectedFlight.operator.logo_small_url"
                      :alt="selectedFlight.operator.name + ' icon'"
                  />
                    <span>{{ selectedFlight.operator.name }}</span>
                  </p>
                </div>
              </div>
            </div>

            <div class="row g-2 mb-4">
              <div class="col-12">
                <h2 class="h6 small fw-bold text-uppercase text-primary mb-1">Aircraft</h2>
              </div>
              <div class="col-12 col-md-4">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Model</h3>
                  <p class="mb-0 fw-bold mb-n1">{{ selectedFlight.aircraft?.model ?? 'N.t.b' }}</p>
                </div>
              </div>
              <div v-if="selectedFlight.aircraft" class="col-6 col-md-4">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Reg.</h3>
                  <p class="mb-0 fw-bold mb-n1">
                    <NuxtLink
                        :to="{ name: 'aircrafts-slug', params: { slug: selectedFlight.aircraft.slug }}"
                        class="inherit fw-bold"
                        :title="selectedFlight.aircraft.registration + ' ' + selectedFlight.aircraft.model">
                      {{ selectedFlight.aircraft.registration }}
                    </NuxtLink>
                  </p>
                </div>
              </div>
              <div class="col-6 col-md-4">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Age</h3>
                  <p class="mb-0 fw-bold mb-n1">{{ selectedFlight.aircraft ? formatAge(selectedFlight.aircraft.age) + ' year' : '-' }}</p>
                </div>
              </div>
            </div>

            <div class="row overflow-hidden mb-4">
              <div class="col-12">
                <ScriptGoogleAdsense
                    data-ad-client="ca-pub-2456044840697680"
                    data-ad-slot="5727997721"
                    data-full-width-responsive="true"
                />
              </div>
            </div>

            <div class="row g-2 mb-4">
              <div class="col-12">
                <h2 class="h6 small fw-bold text-uppercase text-primary mb-1">Other {{ selectedFlight.flight_number }} flights</h2>
              </div>
              <div class="col-12">
                <div class="card no-hover py-2 pb-0 bg-blurry">
                  <div class="table-responsive-md overflow-y-hidden">
                    <table class="table table-hover align-middle position-relative blur mb-0" itemscope itemtype="https://schema.org/Table">
                      <thead>
                      <tr>
                        <th class="small text-uppercase opacity-75 fw-normal pt-0">Date</th>
                        <th class="small text-uppercase opacity-75 fw-normal pt-0">From</th>
                        <th class="small text-uppercase opacity-75 fw-normal pt-0">To</th>
                        <th class="small text-uppercase opacity-75 fw-normal pt-0">Status</th>
                      </tr>
                      </thead>
                      <tbody>
                        <tr v-for="flight in data.slice(0, limitFlights)" :key="flight.scheduled_departure" @click="() => { selectedFlight = flight; scrollTop(); }" :class="{'active' : selectedFlight.scheduled_departure === flight.scheduled_departure}">
                          <th>
                            {{ formatDate(flight.scheduled_departure, true) }}
                          </th>
                          <th>
                            <span class="d-block">{{ flight.origin.iata}}</span>
                            <span class="small opacity-75">{{ flight.origin.city.name}}</span>
                          </th>
                          <th>
                            <span class="d-block">{{ flight.destination.iata}}</span>
                            <span class="small opacity-75">{{ flight.destination.city.name}}</span>
                          </th>
                          <th>
                              <span class="badge rounded-pill small border mb-0 bg-blurry inherit" :class="sanitizeTitle('status-'+ flight.status)">
                                <span class="live-animation" v-if="flight.status === 'Live'">●</span>
                                {{ flight.status }}
                              </span>
                          </th>
                        </tr>
                      </tbody>
                      <tfoot class="blur" v-if="!authStore.user && limitFlights < data.length">
                        <tr v-for="n in 4">
                          <th>
                            {{ moment().format('D MMM YYYY') }}
                          </th>
                          <th>
                            <span class="d-block">AMS</span>
                          </th>
                          <th>
                            <span class="d-block">JFK</span>
                          </th>
                          <th>
                            <span class="badge rounded-pill small border mb-0 bg-blurry inherit status-landed">
                              Landed
                            </span>
                          </th>
                        </tr>
                      </tfoot>
                    </table>
                    <div v-if="!authStore.user && limitFlights < data.length" class="table-disclaimer">
                      <div class="bg-transparent text-center">
                        <NuxtImg
                            class="mb-2"
                            alt="Pilot Emoji"
                            width="30"
                            height="30"
                            loading="lazy"
                            src="img/pilot-emoji.png"
                        />
                        <p class="h3 text-primary">Get Full Access</p>
                        <p class="mb-0">For full access, create a <b>free account</b> or <b>log in</b>.</p>
                        <p class="mb-0">Tap <b>Account</b> in the menu to get started.</p>
                      </div>
                    </div>
                    <div class="text-center mb-3 pt-3 border-top" v-if="authStore.user && limitFlights < data.length">
                      <button @click="increaseLimit" role="button" class="badge bg-primary-darken border rounded-pill">Load more</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="row mb-6 gy-2" itemscope itemtype="https://schema.org/FAQPage">
              <div class="col-12">
                <h2 class="h6 small fw-bold text-uppercase text-primary mb-1">Frequently Asked Questions</h2>
              </div>
              <Question>
                <template v-slot:question>
                  Which airline operates flight number {{ selectedFlight.flight_number }}?
                </template>
                <template v-slot:answer>
                  <p class="mb-0">{{ selectedFlight.airline.short_name }} flight {{ selectedFlight.flight_number }}, departing on {{ moment(selectedFlight.scheduled_departure).format('D MMM YYYY') }}, is operated by {{ selectedFlight.operator.name }}.</p>
                </template>
              </Question>
              <Question>
                <template v-slot:question>
                  What is the destination of flight number {{ selectedFlight.flight_number }}?
                </template>
                <template v-slot:answer>
                  <p class="mb-0">The destination of flight {{ selectedFlight.flight_number }} is {{ selectedFlight.destination.name_en }} in {{ selectedFlight.destination.city.name }} ({{ selectedFlight.destination.city.country.name }}). This <NuxtLink :title="selectedFlight.airline.short_name + ' ' + selectedFlight.origin.city.name + ' to ' + selectedFlight.destination.city.name" :to="{ name: 'flights-between', params: { name: selectedFlight.origin.city.slug_en, iata: selectedFlight.origin.iata.toLowerCase(), toname: selectedFlight.destination.city.slug_en, toiata: selectedFlight.destination.iata.toLowerCase() } }">{{ selectedFlight.airline.short_name }} flight from {{ selectedFlight.origin.city.name }} to {{ selectedFlight.destination.city.name }}</NuxtLink> has an approximate duration of {{ createFormattedTime(selectedFlight.flight_duration) }}.</p>
                </template>
              </Question>
              <Question v-if="selectedFlight.aircraft">
                <template v-slot:question>
                  What aircraft is used for flight {{ selectedFlight.flight_number }} to {{ selectedFlight.destination.city.name }}?
                </template>
                <template v-slot:answer>
                  <p class="mb-0">The flight to {{ selectedFlight.destination.city.name }} with {{ selectedFlight.airline.short_name }} on {{ moment(selectedFlight.scheduled_departure).format('D MMM YYYY') }} is operated using a {{ selectedFlight.aircraft.model }}.</p>
                </template>
              </Question>
              <Question>
                <template v-slot:question>
                  What can I do if my {{ selectedFlight.airline.short_name }} flight to {{ selectedFlight.destination.city.name }} is delayed or canceled? Can I claim compensation?
                </template>
                <template v-slot:answer>
                  <p>
                    If your {{ selectedFlight.airline.short_name }} flight is delayed or canceled, you may be entitled to compensation that can go up to €600 per passenger.
                  </p>
                  <p>The compensation depends on the length of your flight. The further you fly, the higher the compensation and the more money you get back.</p>
                  <ul>
                    <li>Less than 1,500 km: €250.</li>
                    <li>Between 1,500 and 3,500 km: €400.</li>
                    <li>More than 3,500 km: €600.</li>
                  </ul>
                  <p>If your {{ selectedFlight.airline.short_name }} flight to {{ selectedFlight.destination.city.name }} is delayed or canceled, check if you are eligible for compensation. We recommend using AirHelp.com.</p>
                  <a :title="'Calculate ' + selectedFlight.airline.short_name + ' compensation'"
                     :href="'https://www.airhelp.com/en/?utm_medium=affiliate&utm_source=pap&utm_campaign=aff-646c6e21f27d6&utm_content=&a_aid=646c6e21f27d6&a_bid=5c94479f&data1=' + selectedFlight.flight_number + '&chan=rewardsflights'" target="_blank" rel="nofollow noopener" class="btn btn-primary fw-bold mt-2">Calculate your compensation</a>
                </template>
              </Question>
              <Question v-if="selectedFlight.origin.city.country.continent != selectedFlight.destination.city.country.continent">
                <template v-slot:question>
                  How early should I arrive at {{ selectedFlight.origin.name_en }} for flight {{ selectedFlight.flight_number }}?
                </template>
                <template v-slot:answer>
                  <p class="mb-0">For an <a title="What is an intercontinental flight?" href="https://rewards.flights/blog/what-is-an-intercontinental-flight-and-how-early-should-you-arrive-at-the-airport">intercontinental flight</a> from {{ selectedFlight.origin.name_en }} to {{ selectedFlight.destination.name_en }}, it is recommended to arrive at least 3 hours in advance. For specific guidelines from {{ selectedFlight.operator.short_name ?? selectedFlight.operator.name }}, always check the instructions for the most accurate information.</p>
                </template>
              </Question>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>
<script setup lang="ts">
import moment from "moment/moment";
import {useAuthStore} from "~/store/auth";

const route = useRoute()
const runtimeConfig = useRuntimeConfig()

const {data: fetchedData} = await useAsyncData(
    'flight-' + route.params.slug,
    async () => {
      return $fetch(runtimeConfig.public.backendUrl + "/api/v2/flights/" + route.params.slug, {
        headers: {
          'User-Agent': 'RewardsFlightsFrontend/1.0 (https://rewards.flights)',
        }
      })
    }
);

if (!fetchedData.value.flight?.length) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Flight not found'
  })
}

const data = ref(fetchedData.value.flight);

const latestFlightTodayOrNext = computed(() => {
  const now = moment();
  const today = moment().startOf('day').local();

  // Filter vluchten voor alleen vandaag
  const todaysFlights = data.value.filter(flight =>
      moment(flight.scheduled_departure).local().isSame(today, 'day')
  );

  // Controleer of er een vlucht gaande is
  const ongoingFlight = todaysFlights.find(flight =>
      moment(flight.scheduled_departure).local().isSameOrBefore(now) &&
      moment(flight.scheduled_arrival).local().isAfter(now)
  );

  if (ongoingFlight) {
    return ongoingFlight;
  }

  // Zoek naar de meest recente vlucht van vandaag die al geweest is
  const pastFlightsToday = todaysFlights.filter(flight =>
      moment(flight.scheduled_departure).local().isBefore(now)
  ).sort((a, b) => moment(b.scheduled_departure).local().diff(moment(a.scheduled_departure).local()));

  if (pastFlightsToday.length > 0) {
    return pastFlightsToday[0]; // De meest recente vlucht van vandaag die al geweest is
  }

  // Zoek naar de eerstvolgende vlucht na nu
  const futureFlights = data.value
      .filter(flight => moment(flight.scheduled_departure).local().isAfter(now))
      .sort((a, b) => moment(a.scheduled_departure).local().diff(moment(b.scheduled_departure).local()));

  if (futureFlights.length > 0) {
    return futureFlights[0]; // De eerstvolgende toekomstige vlucht
  }

  // Als er geen toekomstige vlucht is, zoek dan naar de meest recente vlucht uit het verleden
  const pastFlights = data.value
      .filter(flight => moment(flight.scheduled_departure).local().isBefore(now))
      .sort((a, b) => moment(b.scheduled_departure).local().diff(moment(a.scheduled_departure).local()));

  return pastFlights.length > 0 ? pastFlights[0] : null; // De meest recente vlucht uit het verleden
});

const _selectedFlight = ref(latestFlightTodayOrNext.value);

const selectedFlight = computed({
  get: () => _selectedFlight.value,
  set: (val) => {
    _selectedFlight.value = val;
  }
});

const limitFlights = ref(5);

const increaseLimit = () => {
  limitFlights.value += 25;
};

const authStore = useAuthStore();

defineOgImage({
  component: 'route',
  description: latestFlightTodayOrNext.value.flight_number + ' • ' + latestFlightTodayOrNext.value.airline.name,
  title: latestFlightTodayOrNext.value.origin.iata + ' - ' + latestFlightTodayOrNext.value.destination.iata,
});

useSeoMeta({
  title: "✈ Flight " + latestFlightTodayOrNext.value.flight_number + " from " + latestFlightTodayOrNext.value.origin.city.name + " (" + latestFlightTodayOrNext.value.origin.iata + ") to " + latestFlightTodayOrNext.value.destination.city.name + " (" + latestFlightTodayOrNext.value.destination.iata + ")",
  ogTitle: "✈ Flight " + latestFlightTodayOrNext.value.flight_number + " from " + latestFlightTodayOrNext.value.origin.city.name + " (" + latestFlightTodayOrNext.value.origin.iata + ") to " + latestFlightTodayOrNext.value.destination.city.name + " (" + latestFlightTodayOrNext.value.destination.iata + ")",
  twitterTitle: "✈ Flight " + latestFlightTodayOrNext.value.flight_number + " from " + latestFlightTodayOrNext.value.origin.city.name + " (" + latestFlightTodayOrNext.value.origin.iata + ") to " + latestFlightTodayOrNext.value.destination.city.name + " (" + latestFlightTodayOrNext.value.destination.iata + ")",
  description: "Flight status of " + latestFlightTodayOrNext.value.airline.name + " flight " + latestFlightTodayOrNext.value.flight_number + " from " + latestFlightTodayOrNext.value.origin.city.name + " to " + latestFlightTodayOrNext.value.destination.city.name + ": real-time information on departure, arrival, delays, and cancellations.",
  ogDescription: "Flight status of " + latestFlightTodayOrNext.value.airline.name + " flight " + latestFlightTodayOrNext.value.flight_number + " from " + latestFlightTodayOrNext.value.origin.city.name + " to " + latestFlightTodayOrNext.value.destination.city.name + ": real-time information on departure, arrival, delays, and cancellations.",
  twitterDescription: "Flight status of " + latestFlightTodayOrNext.value.airline.name + " flight " + latestFlightTodayOrNext.value.flight_number + " from " + latestFlightTodayOrNext.value.origin.city.name + " to " + latestFlightTodayOrNext.value.destination.city.name + ": real-time information on departure, arrival, delays, and cancellations.",
  ogUrl: runtimeConfig.public.baseUrl + "/flights/" + data.value[0].flight_number.toLowerCase(),
  ogLocale: "en",
  ogLocaleAlternate: "nl",
});

useHead({
  htmlAttrs: {
    lang: 'en'
  },
  link: [
    {
      rel: "canonical",
      href: runtimeConfig.public.baseUrl + "/flights/" + data.value[0].flight_number.toLowerCase()
    },
    {
      rel: "alternate",
      hreflang: "x-default",
      href: runtimeConfig.public.baseUrl + "/flights/" + data.value[0].flight_number.toLowerCase()
    },
    {
      rel: "alternate",
      hreflang: "en",
      href: runtimeConfig.public.baseUrl + "/flights/" + data.value[0].flight_number.toLowerCase()
    },
    {
      rel: "alternate",
      hreflang: "nl",
      href: runtimeConfig.public.baseUrl + "/nl/vluchten/" + data.value[0].flight_number.toLowerCase()
    }
  ],
});

useJsonld(() => {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: runtimeConfig.public.baseUrl,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Luchtvaartmaatschappijen',
            item: runtimeConfig.public.baseUrl + '/airlines',
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: data.value[0].airline.name,
            item: runtimeConfig.public.baseUrl + '/airlines/' + data.value[0].airline.slug,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: data.value[0].flight_number,
          },
        ],
      },
      {
        '@type': 'Flight',
        flightNumber: data.value[0].flight_number,
        aircraft: data.value[0].aircraft?.model,
        departureAirport: {
          '@type': 'Airport',
          iataCode: data.value[0].origin.iata,
          name: data.value[0].origin.name_en,
        },
        arrivalAirport: {
          '@type': 'Airport',
          iataCode: data.value[0].destination.iata,
          name: data.value[0].destination.name_en,
        },
      }
    ],
  }
});

const createFormattedTime = (durationInMinutes: string) => computed(() => {
  let duration = moment.duration(durationInMinutes, 'minutes');
  return `${Math.floor(duration.asHours())}h ${duration.minutes()}m`;
});

const createFormattedDistance = (distance: string) => computed(() => {
  if (distance === undefined || distance === null) {
    return;
  }
  return Intl.NumberFormat("en").format(Number(distance)) + ' km';
});

function formatDate(date: string | null, localTime: boolean = false) {
  if (date === null) {
    return '-';
  }

  const parsedDate = moment(date);
  const today = moment().startOf('day');
  const yesterday = moment().subtract(1, 'days').startOf('day');
  const tomorrow = moment().add(1, 'days').startOf('day');

  if (parsedDate.isSame(today, 'day')) {
    return `Today`;
  } else if (parsedDate.isSame(yesterday, 'day')) {
    return `Yesterday`;
  } else if (parsedDate.isSame(tomorrow, 'day')) {
    return `Tomorrow`;
  }

  if(localTime) {
    return parsedDate.format('D MMM YYYY');
  }

  return moment(date).format('D MMM YYYY');
}

function formatTime(date: string | null) {
  if (date === null) {
    return '-';
  }

  return moment.parseZone(date).format('hh:mm A');
}

function getTimezone(date: string | null) {
  if (date === null) {
    return '-';
  }

  return moment.parseZone(date).format('Z');
}

function formatAge(age: string | null) {
  if (age === null) {
    return '-';
  }

  const ageDate = moment(age);
  const now = moment();

  return now.diff(ageDate, 'years');
}

function sanitizeTitle(title: string) {
  let slug = "";
  const titleLower = title.toLowerCase();
  slug = titleLower.replace(/\s*$/g, '');
  slug = slug.replace(/\s+/g, '-');
  return slug;
}

function generateStatus(flight: object) {
  if (flight.status === 'Live') {
    return 'Live (In Air)';
  }
  if (flight.status === 'Landed') {
    if(flight.arrival_delay && flight.arrival_delay > 15){
      return 'Landed with a '+ flight.arrival_delay +'-minute delay';
    }
    return 'Landed';
  }

  return flight.status
}

function generateStatusClass(flight: object){
  if (flight.status === 'Scheduled') {
    return 'text-white';
  } else if (flight.status === 'Landed') {
    if(flight.arrival_delay && flight.arrival_delay > 15){
      return 'text-warning';
    }
    return 'text-success';
  } else if (flight.status === 'Diverted') {
    return 'text-warning';
  } else if (flight.status === 'Canceled') {
    return 'text-danger';
  }

  return 'text-white';
}

const scrollTop = () => {
  window.scrollTo(0, 0)
}

</script>